import type { CourseEnrollment } from '../types';

/**
 * The course id an enrolment refers to, whichever shape it arrived in.
 *
 * `CourseEnrollment.courseId` is genuinely a union — `string | Course` — because
 * the two endpoints that produce enrolments disagree:
 *
 *   • `GET /users/:id/enrollments` calls `.populate('courseId')`, so the id
 *     arrives as a whole course object and lives at `courseId._id`.
 *   • `POST /courses/:id/enroll` returns the raw document, so the id arrives as
 *     a bare string at `courseId`.
 *
 * Comparing the union directly against a course `_id` is the bug this exists to
 * prevent: `{...} === "68f3…"` is an object-vs-string comparison that is always
 * false, so an enrolled learner was shown "Enroll Now" on a course they already
 * owned. It looked like it worked, because the enrol response is the
 * *unpopulated* shape — the button flipped correctly once, then reverted on the
 * next reload when the populated list replaced it, which is exactly the kind of
 * bug that survives manual testing.
 *
 * TypeScript could not catch it: `string` overlaps the union, so `===` is legal.
 * The only durable fix is to stop hand-narrowing at each call site.
 */
export const enrollmentCourseId = (enrollment: Pick<CourseEnrollment, 'courseId'>): string | undefined => {
  const raw = enrollment?.courseId as unknown;
  if (!raw) return undefined;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object' && '_id' in (raw as Record<string, unknown>)) {
    const id = (raw as { _id?: unknown })._id;
    return id == null ? undefined : String(id);
  }
  // An ObjectId that survived serialisation still stringifies to its hex form.
  return String(raw);
};

/**
 * The same id as a total function, for use as an object key.
 *
 * Callers that index a lookup (`certificates[key]`) need a `string`, and an
 * enrolment with no course is a broken record that should simply miss rather
 * than throw. `''` never matches a real id, which is the behaviour those call
 * sites already want.
 */
export const enrollmentCourseKey = (enrollment: Pick<CourseEnrollment, 'courseId'>): string =>
  enrollmentCourseId(enrollment) ?? '';

/** True when `enrollments` contains one for `courseId`, in either shape. */
export const isEnrolledIn = (
  enrollments: Array<Pick<CourseEnrollment, 'courseId'>> | null | undefined,
  courseId: string | undefined
): boolean => {
  if (!courseId || !Array.isArray(enrollments)) return false;
  return enrollments.some((enrollment) => enrollmentCourseId(enrollment) === String(courseId));
};
